import React, {
  useState,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { requestNotificationPermission } from '@app/firebaseMessaging';
import { updateFcmTokenRequest } from '@reducers/auth/AuthAction';
import { createNotificationRequest } from '@reducers/notification/NotificationAction';
import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import useTranslation from '@shared/hooks/useTranslation';

import './NotificationAlertStyle.scss';

const NotificationAlert = () => {
  const i18n = useTranslation('general');
  const dispatch = useDispatch<any>();
  const user = useSelector((state: any) => state.Auth.user);
  const [ isVisible, setIsVisible ] = useState(false);
  const [ loading, setLoading ] = useState(false);

  const userNotifications = user?.notifications?.value;
  const notifications = typeof userNotifications === 'string' ? JSON.parse(user?.notifications?.value) : userNotifications;
  const isNotificationOn = notifications?.notifications?.chatMessages?.browser ?? false;

  const enableSettingsNotification = async () => {
    setLoading(true);
    const payload = {
      notifications: {
        chatMessages: {
          ...notifications?.notifications?.chatMessages,
          browser: true,
        },
      },
    };

    try {
      await dispatch(createNotificationRequest(user?.id, payload)).$promise;
      setLoading(false);
    } catch (error) {
      handleError(error);
      setLoading(false);
    }
  };

  const getBrowserInfo = () => {
    const nav: any = navigator;
    const ua = nav.userAgent;
    const browserData = nav.userAgentData;

    if (browserData) {
      // Newer Chromium browsers support this
      const browser = browserData.brands
        .map((b: any) => b.brand)
        .join(', ');

      if (browser.includes('Google Chrome')) {
        return {
          name: 'Google Chrome',
          tutorial: 'https://support.google.com/chrome/answer/3220216?hl=en&co=GENIE.Platform%3DDesktop&oco=1',
        };
      }

      if (browser.includes('Microsoft Edge')) {
        return {
          name: 'Microsoft Edge',
          tutorial: 'https://support.microsoft.com/en-us/microsoft-edge/manage-website-notifications-in-microsoft-edge-0c555609-5bf2-479d-a59d-fb30a0b80b2b',
        };
      }

      if (browser.includes('Opera')) {
        return {
          name: 'Opera',
          tutorial: 'https://help.opera.com/en/latest/web-preferences/#notifications',
        };
      }

      if (browser.includes('Brave')) {
        return {
          name: 'Brave',
          tutorial: 'https://support.brave.app/hc/en-us/articles/360018205431-How-do-I-change-site-permissions',
        };
      }
    }

    // Fallback for other browsers (not chromium)
    if (ua.includes('Firefox')) {
      return {
        name: 'Firefox',
        tutorial: 'https://support.mozilla.org/en-US/kb/push-notifications-firefox',
      };
    }

    if (ua.includes('Edge')) {
      return {
        name: 'Microsoft Edge',
        tutorial: 'https://support.microsoft.com/en-us/microsoft-edge/manage-website-notifications-in-microsoft-edge-0c555609-5bf2-479d-a59d-fb30a0b80b2b',
      };
    }

    if (ua.includes('Chrome')) {
      return {
        name: 'Google Chrome',
        tutorial: 'https://support.google.com/chrome/answer/3220216?hl=en&co=GENIE.Platform%3DDesktop&oco=1',
      };
    }

    if (ua.includes('Safari') && !ua.includes('Chrome')) {
      return {
        name: 'Safari',
        tutorial: 'https://support.apple.com/guide/safari/manage-website-notifications-sfri40734/mac',
      };
    }

    if (ua.includes('Opera') || ua.includes('OPR')) {
      return {
        name: 'Opera',
        tutorial: 'https://help.opera.com/en/latest/web-preferences/',
      };
    }

    return {
      name: 'Unknown',
      tutorial: '',
    };
  };

  const enableBrowserNotification = async () => {
    try {
      const browser: any = getBrowserInfo();
      const permission = await requestNotificationPermission((token: string) => dispatch(
        updateFcmTokenRequest({ fcm_token: token })),
      );

      // If the permission is not null, it means there is a token and the permission was granted.
      if (permission !== null) {
        await enableSettingsNotification();
        toast.success(i18n.notification_alert.success);
        setIsVisible(false);
      }

      // This means notifications are blocked or denied
      if (permission === null && browser?.tutorial) {
        window.open(browser.tutorial);
      }
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    let isDefaultBrowserNotificationOn = true;

    // Check if browser notifications are denied or default
    if ('Notification' in window) {
      if (Notification.permission === 'default' || Notification.permission === 'denied') {
        isDefaultBrowserNotificationOn = false;
      }
    }

    if (!isNotificationOn || !isDefaultBrowserNotificationOn) {
      setIsVisible(true);
      return;
    }

    setIsVisible(false);
  }, [ isNotificationOn ]);

  if (!isVisible) return null;

  return (
    <div className="notification-alert">
      {loading && (<span>{i18n.notification_alert.loading}</span>)}
      {!loading && (
        <>
          {LocaleService.parseTranslationElement(i18n.notification_alert.title, {
            // eslint-disable-next-line react/no-unstable-nested-components
            buttonLabel: (buttonLabel: string) => (
              <CustomButton
                id="enable-notification"
                className="enable-notification-button"
                onClick={enableBrowserNotification}
              >
                {buttonLabel}
              </CustomButton>
            ),
          })}
        </>
      )}
    </div>
  );
};

export default NotificationAlert;
